import { MAX_SMS_SESSION_RECIPIENTS } from '@provision/conversation-types';
import { useQuery } from '@provision/graphql-client';
import { useCallback, useMemo, useRef, useState } from 'react';
import { formatToPhoneNumber } from 'src/components/FormattedPhoneInput/format-to-phone-number';
import { usePhonesAndEmailsLabels } from 'src/graphql/entities/client-label';
import type { ConversationParticipantItem } from 'src/graphql/entities/conversation-participants';
import {
  buildNumberOptions,
  defaultSmsRecipients,
  sessionRecipients,
  type SmsNumberOption,
  type SmsNumberVerdict,
  type SmsRecipient,
} from 'src/pages/Conversations/Chat/SmsSession/sms-recipients';
import { useSmsSessionContext } from 'src/pages/Conversations/Chat/SmsSession/sms-session-context';
import {
  GET_SMS_SESSION_LINE_QUERY,
  type GetSmsSessionLineQuery,
  type GetSmsSessionLineVars,
} from 'src/pages/Conversations/Chat/SmsSession/sms-session.operations';
import { useNotifications } from 'src/utils/hooks';
import { useConfirmationModal } from 'src/utils/hooks/use-confirmation-modal';
import { useSmsDeliverabilityCheck } from './use-sms-deliverability-check';

export interface SmsRecipientsModel {
  recipients: SmsRecipient[];
  options: SmsNumberOption[];
  /** a live session owns the set; the line shows the live dot and a change asks first */
  isLocked: boolean;
  /** from a confirmed mid-session change until the next session opens; the composer stays in SMS */
  awaitingNewSession: boolean;
  canAdd: boolean;
  sendsFrom: { phoneNumber: string; brandName: string | null } | null;
  replaceRecipient: (index: number, next: SmsRecipient) => Promise<void>;
  addRecipient: (next: SmsRecipient) => Promise<void>;
  removeRecipient: (index: number) => Promise<void>;
  /** lazy Twilio Lookup for the listed numbers nobody has checked yet; once per number per conversation */
  checkNumbers: (options: SmsNumberOption[]) => void;
}

export function useSmsRecipients({
  conversationId,
  participants,
  requesterId,
  enabled,
}: {
  conversationId: string;
  participants: ConversationParticipantItem[];
  requesterId: string | null;
  enabled: boolean;
}): SmsRecipientsModel {
  const hasOpenSession = useSmsSessionContext((v) => v.hasOpenSession);
  const openSessionId = useSmsSessionContext((v) => v.openSessionId);
  const sessionsById = useSmsSessionContext((v) => v.sessionsById);
  const smsChannelsById = useSmsSessionContext((v) => v.smsChannelsById);
  const isLoadingChannels = useSmsSessionContext((v) => v.isLoadingChannels);
  const endSession = useSmsSessionContext((v) => v.endSession);
  const { clientPhoneLabels } = usePhonesAndEmailsLabels();
  const { confirm } = useConfirmationModal();
  const { showErrorAlert } = useNotifications();
  const checkOnce = useSmsDeliverabilityCheck(conversationId);

  const [checked, setChecked] = useState<ReadonlyMap<string, SmsNumberVerdict>>(new Map());
  const [chosen, setChosen] = useState<SmsRecipient[] | null>(null);
  // the session a confirmed change ended; the wait is over once another session opens
  const [endedForChangeId, setEndedForChangeId] = useState<string | null>(null);
  if (endedForChangeId && openSessionId && openSessionId !== endedForChangeId) {
    setEndedForChangeId(null);
  }
  // a new conversation starts from its default; the agent's picks and the checks belong to one conversation
  const prevConversationIdRef = useRef(conversationId);
  if (prevConversationIdRef.current !== conversationId) {
    prevConversationIdRef.current = conversationId;
    setChosen(null);
    setChecked(new Map());
    setEndedForChangeId(null);
  }

  const channels = useMemo(() => [...smsChannelsById.values()], [smsChannelsById]);
  const options = useMemo(
    () =>
      buildNumberOptions({ participants, channels, labels: clientPhoneLabels ?? [], checked }),
    [participants, channels, clientPhoneLabels, checked],
  );
  // no default while channels load: the requester's row may not be the thread's number
  const defaults = useMemo(
    () =>
      isLoadingChannels
        ? []
        : defaultSmsRecipients({ options, channels, participants, requesterId }),
    [isLoadingChannels, options, channels, participants, requesterId],
  );

  const openSession = openSessionId ? sessionsById.get(openSessionId) : undefined;
  const lockedRecipients = useMemo(() => {
    if (!hasOpenSession || !openSession) return null;
    const resolved = sessionRecipients(
      openSession.recipientChannelIds,
      options,
      smsChannelsById,
    );
    // right after a start the participations cache may not know the new channels yet: show what was sent
    return resolved.length < openSession.recipientChannelIds.length
      ? (chosen ?? defaults)
      : resolved;
  }, [hasOpenSession, openSession, options, smsChannelsById, chosen, defaults]);
  const recipients = lockedRecipients ?? chosen ?? defaults;

  const commit = useCallback(
    async (next: SmsRecipient[], title: string) => {
      const digits = next.map((recipient) => recipient.digits);
      // a number twice, or the chip's own number re-picked, changes nothing
      if (
        new Set(digits).size < digits.length ||
        digits.join() === recipients.map((recipient) => recipient.digits).join()
      ) {
        return;
      }
      if (hasOpenSession) {
        const proceed = await confirm({
          title,
          body: 'This ends the live SMS session. The messages so far stay on the timeline, and your next send starts a new session with the numbers you picked.',
          confirmLabel: 'End session and change',
        });
        if (!proceed) return;
        // set before the end lands: the session cache empties first and the composer must not fall back to email
        setEndedForChangeId(openSessionId);
        try {
          await endSession();
        } catch {
          setEndedForChangeId(null);
          showErrorAlert("Couldn't end the SMS session. Try again.");
          return;
        }
      }
      setChosen(next);
    },
    [recipients, hasOpenSession, openSessionId, confirm, endSession, showErrorAlert],
  );

  const replaceRecipient = useCallback(
    (index: number, next: SmsRecipient) => {
      const number = formatToPhoneNumber(next.digits);
      const label = next.kind === 'clientPhone' ? next.label : null;
      const wording = label ? `${label.toLowerCase()} number` : number;
      return commit(
        recipients.map((recipient, i) => (i === index ? next : recipient)),
        next.name ? `Text ${next.name}'s ${wording}?` : `Text ${number}?`,
      );
    },
    [commit, recipients],
  );
  const addRecipient = useCallback(
    (next: SmsRecipient) =>
      commit(
        [...recipients, next],
        `Add ${next.name ?? formatToPhoneNumber(next.digits)} to the text?`,
      ),
    [commit, recipients],
  );
  const removeRecipient = useCallback(
    (index: number) => {
      const gone = recipients[index];
      return commit(
        recipients.filter((_, i) => i !== index),
        `Remove ${gone.name ?? formatToPhoneNumber(gone.digits)} from the text?`,
      );
    },
    [commit, recipients],
  );

  const checkNumbers = useCallback(
    (toCheck: SmsNumberOption[]) => {
      for (const { recipient, verdict } of toCheck) {
        if (recipient.kind === 'typed' || verdict.lineTypeCheckedAt || verdict.smsOptOut) {
          continue;
        }
        const pending = checkOnce(
          recipient.digits,
          recipient.kind === 'clientPhone'
            ? { clientPhoneNumberId: recipient.clientPhoneNumberId }
            : { conversationChannelId: recipient.conversationChannelId },
        );
        void pending?.then((fresh) => {
          // a failed lookup leaves no stamp — the number stays "Unverified" and is retried on the next visit
          if (!fresh?.lineTypeCheckedAt) return;
          setChecked((prev) =>
            new Map(prev).set(recipient.digits, {
              lineType: fresh.lineType,
              lineTypeCheckedAt: fresh.lineTypeCheckedAt,
              smsOptOut: verdict.smsOptOut,
            }),
          );
        });
      }
    },
    [checkOnce],
  );

  const { data: lineData } = useQuery<GetSmsSessionLineQuery, GetSmsSessionLineVars>(
    GET_SMS_SESSION_LINE_QUERY,
    {
      variables: { input: { conversationId } },
      skip: !enabled || !conversationId,
    },
  );
  const line = lineData?.getSmsSessionLine ?? null;
  // a live session texts from the line it started on; the brand is known only for today's line
  const liveLine = openSession?.partnerPhoneNumber ?? null;
  const sendsFrom = liveLine
    ? {
        phoneNumber: liveLine,
        brandName: line?.phoneNumber === liveLine ? line.brandName : null,
      }
    : line;

  const awaitingNewSession = endedForChangeId !== null && !hasOpenSession;

  return {
    recipients,
    options,
    isLocked: lockedRecipients !== null,
    awaitingNewSession,
    canAdd: recipients.length < MAX_SMS_SESSION_RECIPIENTS,
    sendsFrom,
    replaceRecipient,
    addRecipient,
    removeRecipient,
    checkNumbers,
  };
}
